(() => {
  let fontRecoverySequence = 0;
  class LatestLetteringPreview {
    constructor(loadFont) { this.loadFont = loadFont; this.revision = 0; }
    invalidate() { this.revision += 1; }
    async resolve(selection) {
      const revision = ++this.revision;
      const value = { name: String(selection.name || ''), font: String(selection.font || ''), material: String(selection.material || '') };
      if (!/^[A-Za-z]{1,11}$/.test(value.name)) return { status: 'name', revision };
      if (!/^(?:[1-9]|[12][0-9]|30)$/.test(value.font)) return { status: 'font', revision };
      try {
        await this.loadFont(value.font);
        return revision === this.revision ? { status: 'ready', revision, ...value } : { status: 'stale', revision };
      } catch (error) {
        return { status: revision === this.revision ? (error?.code === 'FONT_UNAVAILABLE' ? 'unavailable' : 'error') : 'stale', revision };
      }
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { LatestLetteringPreview };
  if (typeof window === 'undefined' || !window.customElements || customElements.get('auriani-lettering-preview')) return;
  class AurianiLetteringPreview extends HTMLElement {
    connectedCallback() {
      this.fontJobs = new Map();
      this.failedFonts = new Set();
      this.loadedFamilies = new Map();
      try { this.fontSources = JSON.parse(document.querySelector('[data-lettering-font-sources]')?.textContent || '{}'); } catch { this.fontSources = {}; }
      this.coordinator = new LatestLetteringPreview(font => this.loadFont(font));
      this.output = this.querySelector('[data-lettering-word]');
      this.stage = this.querySelector('[data-lettering-stage]');
      this.status = this.querySelector('[data-lettering-status]');
      this.retry = this.querySelector('[data-lettering-retry]');
      this.scene = this.querySelector('[data-necklace-svg]');
      this.sceneContent = this.querySelector('[data-necklace-content]');
      this.view = this.view || 'whole';
      this.geometryKey = null;
      this.controller?.abort();
      this.controller = new AbortController();
      this.retry.addEventListener('click', () => {
        const previews = this.closest('auriani-product')?.querySelectorAll('auriani-lettering-preview') || [this];
        previews.forEach(preview => {
          if (preview === this || preview.dataset.state === 'error' && preview.selection?.font === this.selection?.font) {
            preview.key = ''; preview.update(preview.selection || {});
          }
        });
      }, { signal: this.controller.signal });
      this.querySelectorAll('[data-necklace-view]').forEach(button => button.addEventListener('click', () => this.setView(button.dataset.necklaceView), { signal: this.controller.signal }));
      if (window.ResizeObserver) { this.observer = new ResizeObserver(() => this.fit()); this.observer.observe(this.stage); }
      const product = this.closest('auriani-product');
      if (product?.state) this.update({ name: product.state.name, font: product.state.font, material: product.state.variant?.options[0], chain: product.state.variant?.options[1], length: product.state.variant?.options[2] });
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.coordinator?.invalidate(); this.renderer?.destroy(); this.renderer = null; }
    loadFont(font) {
      if (!(this.dataset.availableFonts || '').split(',').includes(font)) return Promise.reject(Object.assign(new Error('Supplier font import not verified'), { code: 'FONT_UNAVAILABLE' }));
      if (!this.fontJobs.has(font)) {
        const promise = new Promise((resolve, reject) => {
          if (!document.fonts) { reject(new Error('Fonts unavailable')); return; }
          let expired = false;
          const timer = setTimeout(() => { expired = true; reject(new Error('Font timeout')); }, 8000);
          // A CSS-connected FontFace stays failed after a network error. Retry the same
          // verified supplier file as a fresh face, rather than reusing its rejected promise.
          const family = this.failedFonts.has(font) && this.fontSources[font] && window.FontFace ? 'AurianiFont' + font + 'Recovery' + (++fontRecoverySequence) : null;
          const freshFace = family ? new FontFace(family, 'url(' + JSON.stringify(this.fontSources[font]) + ')', { weight: '400', style: 'normal', display: 'swap' }) : null;
          const loading = freshFace ? freshFace.load().then(face => [face]) : document.fonts.load('72px AurianiFont' + font, 'Auriani');
          loading.then(faces => {
            clearTimeout(timer);
            if (expired) return;
            if (faces.length && faces.every(face => face.status === 'loaded')) {
              if (freshFace) { document.fonts.add(freshFace); this.loadedFamilies.set(font, family); }
              resolve();
            }
            else reject(new Error('Font not loaded'));
          }, error => { clearTimeout(timer); reject(error); });
        }).catch(error => { this.fontJobs.delete(font); this.failedFonts.add(font); throw error; });
        this.fontJobs.set(font, promise);
      }
      return this.fontJobs.get(font);
    }
    async update(selection) {
      if (!this.coordinator) return;
      this.selection = { ...selection };
      const key = JSON.stringify(this.selection);
      if (key === this.key) return;
      this.key = key;
      this.output.hidden = true;
      this.output.setAttribute('hidden', '');
      this.sceneContent?.setAttribute('hidden', '');
      this.renderer?.hide();
      this.querySelector('[data-necklace-view="detail"]')?.setAttribute('disabled', '');
      this.retry.hidden = true;
      this.dataset.state = 'loading';
      this.stage.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      const result = await this.coordinator.resolve(this.selection);
      if (result.status === 'stale' || !this.isConnected) return;
      this.dataset.state = result.status;
      this.stage.setAttribute('aria-busy', 'false');
      if (result.status !== 'ready') {
        this.status.textContent = result.status === 'name' ? this.dataset.enterName : result.status === 'font' ? this.dataset.chooseFont : result.status === 'unavailable' ? this.dataset.unavailable : this.dataset.error;
        this.retry.hidden = result.status !== 'error';
        return;
      }
      this.output.textContent = result.name;
      this.output.style.fontFamily = this.loadedFamilies.get(result.font) || 'AurianiFont' + result.font;
      this.dataset.finish = result.material.includes('Rose') ? 'rose' : result.material.includes('Gold') ? 'gold' : result.material.includes('Silver') ? 'silver' : 'neutral';
      this.output.hidden = false;
      this.output.removeAttribute('hidden');
      this.status.textContent = this.dataset.style + ' ' + result.font + ' · ' + result.material;
      if (this.scene) {
        this.necklace = window.AurianiNecklace?.configuration(this.selection);
        if (!this.necklace) {
          this.dataset.state = 'unavailable';
          this.status.textContent = this.dataset.sceneUnavailable;
          return;
        }
        this.sceneContent.removeAttribute('hidden');
        this.status.textContent += ' · ' + this.necklace.chain + ' · ' + this.necklace.length;
        this.querySelector('[data-necklace-title]').textContent = this.dataset.sceneDescription + ' ' + result.name + ' · ' + this.status.textContent;
        this.querySelector('[data-necklace-view="detail"]').removeAttribute('disabled');
      }
      this.fit();
    }
    fit() {
      if (this.scene) { this.drawNecklace(); return; }
      if (!this.output || this.output.hidden || !this.stage.clientWidth) return;
      const size = ['configurator', 'saved'].includes(this.dataset.context) ? 48 : 72;
      this.output.style.fontSize = size + 'px';
      const available = Math.max(1, this.stage.clientWidth - 48);
      if (this.output.scrollWidth > available) this.output.style.fontSize = Math.max(16, Math.floor(size * available / this.output.scrollWidth)) + 'px';
    }
    drawNecklace() {
      if (!this.necklace || this.dataset.state !== 'ready' || !this.isConnected) return;
      const config = this.necklace;
      this.output.style.fontSize = '72px';
      this.output.removeAttribute('transform');
      // Measure the loaded supplier glyphs; no substitute font or guessed name width.
      const box = this.output.getBBox();
      if (!(box.width > 0 && box.height > 0)) return; // Hidden review/mobile scenes are measured on reveal.
      const scale = Math.min(1.5, 270 / box.width, 66 / box.height);
      const width = box.width * scale, height = box.height * scale;
      this.output.setAttribute('transform', 'translate(' + (300 - (box.x + box.width/2)*scale) + ' ' + (config.pendantY - (box.y + box.height/2)*scale) + ') scale(' + scale + ')');
      ['dark', 'light', 'mid', 'shine'].forEach((key, i) => this.style.setProperty('--necklace-' + key, config.palette[i]));
      const geometryKey = JSON.stringify([config.style, config.inches, width, height]);
      const geometry = geometryKey === this.geometryKey ? this.geometry : window.AurianiNecklace.chainLinks(config, width, height);
      if (geometryKey !== this.geometryKey) {
      const chain = this.querySelector('[data-necklace-chain]');
      const fragment = document.createDocumentFragment();
      geometry.links.forEach(link => {
        const element = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        Object.entries({ x: -link.length/2, y: -link.width/2, width: link.length, height: link.width, rx: link.square ? .6 : link.width/2, transform: 'translate(' + link.x.toFixed(2) + ' ' + link.y.toFixed(2) + ') rotate(' + link.angle.toFixed(2) + ')' }).forEach(([key, value]) => element.setAttribute(key, value));
        fragment.append(element);
      });
      chain.replaceChildren(fragment);
      this.querySelector('[data-necklace-attachments]').replaceChildren(...geometry.attachments.map(point => {
        const ring = document.createElementNS('http://www.w3.org/2000/svg', 'ellipse');
        Object.entries({ cx: point.x, cy: point.y, rx: 6, ry: 4 }).forEach(([key, value]) => ring.setAttribute(key, value));
        return ring;
      }));
      this.geometryKey = geometryKey;
      this.geometry = geometry;
      }
      this.dataset.chain = config.style;
      this.dataset.length = String(config.inches);
      if (window.AurianiNecklace3D && this.querySelector('[data-necklace-webgl]') && this.stage.clientWidth) {
        this.renderer ||= new window.AurianiNecklace3D(this);
        this.renderer.update({ name: this.output.textContent, family: this.output.style.fontFamily, box, scale, config, geometry });
      }
      this.setView(this.view);
    }
    setView(view) {
      if (!this.scene) return;
      this.view = view === 'detail' ? 'detail' : 'whole';
      this.scene.setAttribute('viewBox', this.view === 'detail' && this.necklace ? '130 ' + (this.necklace.pendantY - 105) + ' 340 190' : '0 0 600 460');
      this.querySelectorAll('[data-necklace-view]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.necklaceView === this.view)));
      this.renderer?.setView(this.view);
    }
  }
  customElements.define('auriani-lettering-preview', AurianiLetteringPreview);
})();
